/**
 * Pdt_options カスタムスクロールバーUI
 *
 * 商品詳細ページ (sections/product-template.liquid) のバリエーション一覧
 * (.Pdt_options) のネイティブスクロールバーを非表示にし、独自スタイルの
 * スクロールバーUIをオーバーレイ表示するスクリプト。
 *
 * - PC: 常時表示・thumb のドラッグでスクロール可能
 * - モバイル (max-width: 767px): CSSで非表示・OS標準のスクロール挙動
 * - スクロール不要時 (scrollHeight <= clientHeight) は自動非表示
 *
 * 期待する DOM 構造:
 *   <div class="Pdt_optionsWrap">
 *     <div class="Pdt_options">...バリエーション一覧...</div>
 *     <div class="Pdt_options_scrollbar">
 *       <div class="Pdt_options_scrollbar_thumb"></div>
 *     </div>
 *   </div>
 *
 * 関連スタイル: assets/product-options-scrollbar.css
 * (.Pdt_options 自身のレイアウト・スクロール機能関連は assets/app.css 側)
 */
(function () {
  'use strict';

  const SELECTORS = {
    wrap: '.Pdt_optionsWrap',
    content: '.Pdt_options',
    scrollbar: '.Pdt_options_scrollbar',
    thumb: '.Pdt_options_scrollbar_thumb',
  };

  const MIN_THUMB_HEIGHT = 30;
  const RESIZE_DEBOUNCE_MS = 100;

  /**
   * 連続呼び出しを wait ms だけ遅延させ、末尾の1回だけ実行する汎用デバウンス。
   */
  const debounce = (fn, wait) => {
    let timerId;
    return (...args) => {
      clearTimeout(timerId);
      timerId = setTimeout(() => fn(...args), wait);
    };
  };

  /**
   * 1つの .Pdt_optionsWrap 要素にカスタムスクロールバーUIを初期化する。
   * イベントリスナーの登録、初期 thumb サイズ・位置の計算までを行う。
   */
  const initScrollbar = (wrap) => {
    const content = wrap.querySelector(SELECTORS.content);
    const scrollbar = wrap.querySelector(SELECTORS.scrollbar);
    const thumb = wrap.querySelector(SELECTORS.thumb);

    if (!content || !scrollbar || !thumb) {
      console.error('[product-options-scrollbar] required elements not found in', wrap);
      return;
    }

    let isDragging = false;
    let dragStartY = 0;
    let dragStartScrollTop = 0;

    /**
     * thumb のサイズ・位置算出に必要なスクロール領域の寸法をまとめて取得する。
     */
    const getMetrics = () => {
      const trackHeight = scrollbar.clientHeight;
      const { scrollHeight, clientHeight } = content;
      const ratio = clientHeight / scrollHeight;
      const thumbHeight = Math.max(trackHeight * ratio, MIN_THUMB_HEIGHT);
      const maxThumbTop = trackHeight - thumbHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      return { trackHeight, scrollHeight, clientHeight, thumbHeight, maxThumbTop, maxScrollTop };
    };

    /**
     * コンテンツの scrollTop に応じて thumb のサイズと位置を更新する。
     * スクロール不要な場合は is-scrollable クラスを外してスクロールバーを非表示にする。
     */
    const update = () => {
      const { scrollHeight, clientHeight, thumbHeight, maxThumbTop, maxScrollTop } = getMetrics();

      if (scrollHeight <= clientHeight) {
        wrap.classList.remove('is-scrollable');
        return;
      }
      wrap.classList.add('is-scrollable');

      const thumbTop = (content.scrollTop / maxScrollTop) * maxThumbTop;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${thumbTop}px)`;
    };

    /**
     * thumb 上でポインタが押されたときの処理。ドラッグ開始状態に遷移し、
     * setPointerCapture で以降の pointermove / pointerup を thumb で受け取る。
     */
    const onPointerDown = (e) => {
      isDragging = true;
      dragStartY = e.clientY;
      dragStartScrollTop = content.scrollTop;
      thumb.classList.add('is-dragging');
      thumb.setPointerCapture(e.pointerId);
      e.preventDefault();
    };

    /**
     * ドラッグ中のポインタ移動に応じて、コンテンツの scrollTop を更新する。
     * 移動量を thumb トラック上の比率に換算し、スクロール量に変換する。
     */
    const onPointerMove = (e) => {
      if (!isDragging) return;
      const { maxThumbTop, maxScrollTop } = getMetrics();
      if (maxThumbTop <= 0) return;
      const deltaY = e.clientY - dragStartY;
      const deltaScrollTop = (deltaY / maxThumbTop) * maxScrollTop;
      content.scrollTop = dragStartScrollTop + deltaScrollTop;
    };

    /**
     * ドラッグ終了処理。pointerup / pointercancel の両方で呼ばれ、
     * pointer capture を解放してドラッグ状態をクリアする。
     */
    const onPointerUp = (e) => {
      if (!isDragging) return;
      isDragging = false;
      thumb.classList.remove('is-dragging');
      if (thumb.hasPointerCapture && thumb.hasPointerCapture(e.pointerId)) {
        thumb.releasePointerCapture(e.pointerId);
      }
    };

    thumb.addEventListener('pointerdown', onPointerDown);
    thumb.addEventListener('pointermove', onPointerMove);
    thumb.addEventListener('pointerup', onPointerUp);
    thumb.addEventListener('pointercancel', onPointerUp);

    content.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', debounce(update, RESIZE_DEBOUNCE_MS));
    // 画像の遅延ロードで scrollHeight が後から変わり初期 thumb サイズがズレる場合は、
    // window.addEventListener('load', update) を追加すれば画像ロード完了後に再計算される。

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(update);
      // content は max-height 固定のため、子要素の動的増減で scrollHeight が変化しても
      // この observe は発火しない。子の変化に即時追従したい場合は MutationObserver を併用する:
      //   new MutationObserver(update).observe(content, { childList: true, subtree: true, attributes: true });
      resizeObserver.observe(content);
    }

    update();
  };

  /**
   * ページ内の全 .Pdt_optionsWrap を初期化する。
   */
  const init = () => {
    document.querySelectorAll(SELECTORS.wrap).forEach(initScrollbar);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
